import React, { useMemo, useEffect, useState } from 'react';
import * as d3 from 'd3';
import format from 'date-fns/format';
import { Typography } from '@mui/material';
import useStores from '../../stores/useStores';
import agent from '../../lib/agent';
import LoadingSpinner from '../general/LoadingSpinner';

const margin = { top: 10, right: 50, bottom: 30, left: 50 };

const Rectangle = ({
  x,
  y,
  width,
  height,
  d,
  setTooltip,
  colorScale,
  calculateTooltipPosition
}) => {
  const [mouseOver, setMouseOver] = useState(false);

  return (
    <rect
      key={x + y}
      r={0}
      x={x}
      y={y}
      width={width - 2}
      height={height - 2}
      calculateTooltipPosition
      opacity={1}
      fill={colorScale(d.value)}
      rx={0}
      strokeWidth={mouseOver ? 5 : 8}
      stroke={mouseOver ? '#2196f3' : 'none'}
      onMouseEnter={() => {
        const { xPos, yPos } = calculateTooltipPosition(x, y);
        setMouseOver(true);
        setTooltip({
          xLabel: d.x,
          yLabel: d.y,
          xPos,
          yPos,
          // xPos: x + xScale.bandwidth() + margin.left - 200,
          // yPos: y + xScale.bandwidth() / 2 + margin.top,
          value: Math.round(d.value * 100) / 100,
          consumption: Math.round(d.consumption * 100) / 100,
          production: d.production
        });
      }}
      onMouseOut={() => {
        setMouseOver(false);
        setTooltip(null);
      }}
      // containerRef={containerRef}
      cursor="pointer"
    />
  );
};

const Heatmap = props => {
  const { buildingDetailStore } = useStores();
  const { height, buildingId, projectId } = props;
  const [tooltip, setTooltip] = useState(null);
  const [res, setRes] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState((window.innerWidth - 550) / 1.07);

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  useEffect(() => {
    const handleResize = () => {
      setWidth((window.innerWidth - 500) / 1.07);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchData = async (buildingId, projectId) => {
      if (buildingId) {
        await buildingDetailStore.getPredictions(buildingId);
        // setInitialResBuilding(buildingDetailStore.predictions);
        const tempVar = buildingDetailStore.predictions;
        setRes(tempVar);
        setLoading(false);
      } else if (projectId) {
        const tempVar = await agent.Project.getProjectPredictions(projectId);
        setRes(tempVar.data.data);
        setLoading(false);
      }
    };
    fetchData(buildingId, projectId);
  }, [buildingId, projectId]);

  useEffect(() => {
    const formatData = () => {
      const result = [];
      if (res) {
        const data = res;
        const netLoad = data.find(item => item.id === 'net_load');
        const loadCurve = data.find(item => item.id === 'load_curve');
        const chp = data.find(item => item.id === 'chp');
        const solar = data.find(item => item.id === 'solar');
        const fuelCell = data.find(item => item.id === 'fuel_cell');
        const pvWatts = data.find(item => item.id === 'pvwatts');

        for (let i = 0; i < netLoad?.data.length; i += 1) {
          const timestamp = netLoad.data[i].x;
          const date = new Date(timestamp);
          const day = format(date, 'd. MMM');
          const consumption = loadCurve.data[i].y;

          /*
            Old projects don't have the technology breakdown (solar, chp, fuel cell)
            but instead have a pvWatts data which is the solar production. So we use that instead.
          */

          const production = pvWatts
            ? pvWatts.data[i].y
            : chp.data[i].y + solar.data[i].y + fuelCell.data[i].y;

          const net = netLoad.data[i].y;

          result.push({
            x: format(date, 'h a'),
            y: day,
            value: net,
            consumption,
            production
          });
        }
      }
      setData(result);
    };
    formatData();
  }, [res]);

  const boundsWidth = width - margin.right - margin.left;
  const boundsHeight = height - margin.top - margin.bottom;

  const allYGroups = useMemo(() => [...new Set(data.map(d => d.y))], [data]);
  const allXGroups = useMemo(() => [...new Set(data.map(d => d.x))], [data]);
  const maxValue = useMemo(() => d3.max(data.map(d => parseFloat(d.value))), [
    data
  ]);
  const minValue = useMemo(() => d3.min(data.map(d => parseFloat(d.value))), [
    data
  ]);

  const xScale = useMemo(() => {
    return d3
      .scaleBand()
      .range([0, boundsWidth])
      .domain(allXGroups)
      .padding(0.01);
  }, [data, width]);

  const yScale = useMemo(() => {
    return d3
      .scaleBand()
      .range([boundsHeight, 0])
      .domain(allYGroups)
      .padding(0.01);
  }, [data, height]);

  // const colorScale = d3
  //   .scaleSequential()
  //   .interpolator(d3.interpolateInferno)
  //   .domain([min, max]);

  function colorScale(value) {
    const min = minValue - 1;
    const max = maxValue + 1;
    if (value <= min) {
      return 0;
    }
    if (value >= max) {
      return 1;
    }
    const v = (value - min) / (max - min);
    const color = `rgba(59, 130, 246, ${v})`;
    return color;
  }

  const calculateTooltipPosition = (x, y) => {
    const tooltipWidth = 800;
    const tooltipHeight = 100;
    const adjustedXPos = x + xScale.bandwidth() + margin.left;
    const adjustedYPos = y + xScale.bandwidth() / 2 + margin.top;

    const xPos =
      adjustedXPos + tooltipWidth > screenWidth
        ? screenWidth - tooltipWidth
        : adjustedXPos;

    const yPos =
      adjustedYPos + tooltipHeight > screenHeight
        ? screenHeight - tooltipHeight
        : adjustedYPos < 0
        ? 0
        : adjustedYPos;
    return { xPos, yPos };
  };

  // Build the rectangles
  const allShapes = data.map(d => {
    const x = xScale(d.x);
    const y = yScale(d.y);

    if (d.value === null || !x || !y) {
      return;
    }
    const width = xScale.bandwidth();
    const height = yScale.bandwidth();
    return (
      <Rectangle
        key={`${x}${y}`}
        colorScale={colorScale}
        setTooltip={setTooltip}
        calculateTooltipPosition={calculateTooltipPosition}
        width={width}
        height={height}
        x={x}
        y={y}
        d={d}
      />
    );
  });

  const xLabels = allXGroups.map(name => {
    const x = xScale(name);

    if (!x) {
      return null;
    }

    return (
      <text
        key={`${x}text`}
        x={x + xScale.bandwidth() / 2}
        y={boundsHeight + 10}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
      >
        {name}
      </text>
    );
  });

  const yLabels = allYGroups.map(name => {
    const y = yScale(name);

    if (!y) {
      return null;
    }

    return (
      <text
        key={`${y}text`}
        x={-5}
        y={y + yScale.bandwidth() / 2}
        textAnchor="end"
        dominantBaseline="middle"
        fontSize={10}
      >
        {name}
      </text>
    );
  });

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div
          // className="container"
          style={{
            height: '100%',
            width: '100%',
            position: 'relative'
          }}
          //  ref={containerRef}
        >
          {tooltip && (
            <div
              className="tooltip"
              style={{
                backgroundColor: 'white',
                position: 'absolute',
                left: tooltip.xPos,
                top: tooltip.yPos,
                pointerEvents: 'none',
                padding: '10px',
                boxShadow: '0 0 5px rgba(0,0,0,0.3)',
                borderRadius: '5px'
              }}
            >
              <Typography variant="body2" center>
                Consumption: {tooltip.consumption} kWh
              </Typography>
              <Typography variant="body2" center>
                Production: {tooltip.production} kWh
              </Typography>
              <Typography variant="body2" center>
                Net: {tooltip.value} kWh
              </Typography>
              <Typography variant="body2" center>
                Day: {tooltip.yLabel}
              </Typography>
              <Typography variant="body2" center>
                Hour: {tooltip.xLabel}
              </Typography>
            </div>
          )}

          <svg width={width} height={height}>
            <g
              width={boundsWidth}
              height={boundsHeight}
              transform={`translate(${[margin.left, margin.top].join(',')})`}
            >
              {allShapes}
              {xLabels}
              {yLabels}
            </g>
          </svg>
        </div>
      )}
    </>
  );
};
export default Heatmap;
